import { ArticleItemStateProvider } from "@tm/components"
import { FastCalculatorOeArticleItemState, OE } from "@tm/models"
import { PropsWithChildren, useCallback, useEffect, useMemo, useState } from "react"
import { AvailabilityWrapper as AvailabilityComponent } from "@bundles/erp"
import { useUser } from "@tm/context-distribution"
import { useHandleRemoveFromBasket, useLoadOePartErpInfos, useOeArticleOptions } from "@bundles/parts/components/ListV2/hooks"
import { CalcArticle } from "@tm/data/fastCalculator"
import { FastCalculatorListType, useParticularContext } from "../../PureList/ContextProviders/Particular/ParticularContext"
import { useArticleListFeaturesDisable } from "../ArticleListConfiguration/ArticleListFeaturesDisable"
import { useOePartsListParamsContext } from "../OeParts/ContextProvider/ListParams"
import { useErpInfosContext } from "../ContextProvider"
import { useOeBasketQuantities } from "../ContextProvider/BasketQuantities"
import { emptyFunc, isSameOeArticle, mapOeArticleDtoFromOePart } from "../helpers"

type FastCalculatorOeArticleItemStateProviderProps = PropsWithChildren<{
    article: OE.OePart
    articleTypeKey?: string
    fastCalculatorArticle?: CalcArticle
}>

export function FastCalculatorOeArticleItemStateProvider({
    article,
    children,
    articleTypeKey,
    fastCalculatorArticle,
}: FastCalculatorOeArticleItemStateProviderProps) {
    const [quantity, setQuantity] = useState<number>(article?.quantity || 1)
    const articleList = useMemo(() => [article], [article])

    const options = useOeArticleOptions()
    const particularContext = useParticularContext<FastCalculatorListType>()
    const disableReplaceButton = useArticleListFeaturesDisable((x) => x.disableReplaceButton)

    const {
        startParams: { replaceButtonBundle },
    } = useOePartsListParamsContext()
    const erpInfosData = useErpInfosContext()
    const loadOePartErpInfos = useLoadOePartErpInfos(erpInfosData)
    const { userSettings } = useUser()

    const handleReplaceArticle = useCallback(() => {
        if (disableReplaceButton) {
            return
        }

        particularContext?.handleReplaceButton(fastCalculatorArticle ? fastCalculatorArticle.artListInput : article)
    }, [disableReplaceButton, particularContext, fastCalculatorArticle, replaceButtonBundle, article])

    const { basketQuantities, updateBasketQuantities } = useOeBasketQuantities(articleList, true, articleTypeKey)

    const handleRemoveFromBasketInternal = useHandleRemoveFromBasket()

    useEffect(() => {
        // Initially load erp infos for OE part
        loadOePartErpInfos([{ oePart: article, quantity: article?.quantity || 1 }])
    }, [article, loadOePartErpInfos])

    const basketQuantity = useMemo(() => {
        const oeArticleDto = mapOeArticleDtoFromOePart(article)
        return basketQuantities.find((q) => isSameOeArticle(q.article, oeArticleDto))
    }, [basketQuantities, article])

    const handleChangeQuantity = useCallback(
        (value: number, loadErpInfo = true) => {
            if (fastCalculatorArticle) {
                particularContext?.handleChangeQuantity(fastCalculatorArticle.quantity, value)
            }
            setQuantity(value)
            if (loadErpInfo) {
                loadOePartErpInfos([{ oePart: article, quantity: value }])
            }
        },
        [article, fastCalculatorArticle, loadOePartErpInfos, particularContext?.handleChangeQuantity]
    )

    const handlePriceChange = useCallback(
        (value: number) => {
            if (fastCalculatorArticle) {
                particularContext?.handleChangeQuantity(fastCalculatorArticle.price, value)
            }
        },
        [fastCalculatorArticle, particularContext?.handleChangeQuantity]
    )

    const handleRemoveFromBasket = useCallback(async () => {
        if (basketQuantity?.articleQuantities?.allPartItemIds) {
            await handleRemoveFromBasketInternal(basketQuantity?.articleQuantities?.allPartItemIds)
            updateBasketQuantities(articleList)
        }
    }, [basketQuantity, handleRemoveFromBasketInternal, updateBasketQuantities, articleList])

    const handleRemoveOeArticle = useCallback(() => {
        if (fastCalculatorArticle) {
            particularContext?.handleArticleChange?.(fastCalculatorArticle.removeInput)
        }
    }, [fastCalculatorArticle, particularContext])

    const state: FastCalculatorOeArticleItemState = useMemo(
        () => ({
            type: "OE",
            article,
            isVehicleDependent: false,
            options,
            quantity,
            originalQuantity: quantity,
            basketQuantity,
            oeArticleNo: article.number,
            wholesalerArticleNumber: article.traderNumber ?? "",
            thumbnailUrl: "/styles/base/images/oe-part-logo.svg",
            manufacturerThumbnail: article.thumbnailUrl,
            showReplaceButton: !!replaceButtonBundle && !disableReplaceButton,
            addToCostEstimationButtonDisabled: true,
            addToBasketButtonDisabled: true,
            handleAddToBasket: emptyFunc,
            handleChangeQuantity,
            handlePriceChange,
            handleRemoveFromBasket,
            handleRemoveOeArticle,
            AvailabilityComponent,
            handleReplaceArticle,
            selection: undefined,
            currencySymbol: userSettings?.hourlyRatesCurrencyCodeSymbol,
            currencyCode: userSettings?.hourlyRatesCurrencyCode,
            articlePrice: fastCalculatorArticle?.price.value,
            articleQuantity: fastCalculatorArticle?.quantity.value ?? 1,
            showPriceInputField: true,
        }),
        [
            article,
            options,
            quantity,
            basketQuantity,
            replaceButtonBundle,
            disableReplaceButton,
            handleChangeQuantity,
            handlePriceChange,
            handleRemoveFromBasket,
            handleRemoveOeArticle,
            userSettings?.hourlyRatesCurrencyCodeSymbol,
            userSettings?.hourlyRatesCurrencyCode,
            fastCalculatorArticle?.price.value,
            fastCalculatorArticle?.quantity.value,
            handleReplaceArticle,
        ]
    )

    return <ArticleItemStateProvider value={state}>{children}</ArticleItemStateProvider>
}
